import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

import { PreviewPlaceholder } from '../../../components/wizard';
import { useIframeReveal } from '../../../lib/iframePreview';
import { MOBILE } from '../../mobile.tokens';
import { Overlay, Header, CloseButton, CloseLabel, CloseGlyph, Body, Frame } from './MobileSitePreview.styles';

export type MobileSitePreviewProps = {
  open: boolean;
  url: string;
  /** Brand body background (solid) — the flat colour shown with the loader while
   *  the site loads, so the preview never flashes a black screen. */
  bgColor?: string;
  onClose: () => void;
};

const CloseX = (): React.ReactElement => (
  <svg width="30" height="30" viewBox="0 0 30 30" aria-hidden>
    <path
      d="M21.9736 19.8918C22.5422 20.4607 22.5423 21.4039 21.9736 21.9728C21.405 22.5416 20.4623 22.5415 19.8936 21.9728L8.03223 10.1066L8.0293 10.1046C7.73853 9.83317 7.59968 9.44188 7.59961 9.06656C7.59961 8.69332 7.73761 8.32027 8.03223 8.02555C8.60084 7.45715 9.54272 7.45715 10.1113 8.02555L21.9736 19.8918Z"
      fill={MOBILE.black}
    />
    <path
      d="M21.9736 8.02555C22.5423 8.59442 22.5422 9.53767 21.9736 10.1066L10.1113 21.9728C9.54272 22.5412 8.60084 22.5412 8.03223 21.9728C7.73761 21.6781 7.59961 21.305 7.59961 20.9318C7.59968 20.5587 7.73769 20.1864 8.03223 19.8918L19.8936 8.02555C20.4623 7.45687 21.405 7.45678 21.9736 8.02555Z"
      fill={MOBILE.black}
    />
  </svg>
);

/**
 * Full-screen mobile site preview: the live site full-bleed with rounded top
 * corners under a slim header whose only control is "Close preview" + an orange
 * circle with a black X. Fades in on mount.
 */
export const MobileSitePreview = ({
  open,
  url,
  bgColor,
  onClose,
}: MobileSitePreviewProps): React.ReactElement | null => {
  const { t } = useTranslation();
  const [shown, setShown] = useState(false);
  const { revealed, onLoad } = useIframeReveal(url, `${open}|${url}`);

  useEffect(() => {
    if (!open) {
      setShown(false);
      return undefined;
    }
    const raf = requestAnimationFrame(() => setShown(true));
    return () => cancelAnimationFrame(raf);
  }, [open]);

  if (!open) return null;

  return createPortal(
    <Overlay $shown={shown}>
      <Header>
        <CloseButton type="button" onClick={onClose} data-ga-id="cfg-mobile-preview-close" aria-label={t('Close preview')}>
          <CloseLabel text="Close preview" tag="span" />
          <CloseGlyph>
            <CloseX />
          </CloseGlyph>
        </CloseButton>
      </Header>

      <Body>
        {url ? (
          <Frame title={t('Site preview')} src={url} onLoad={onLoad} $shown={revealed} />
        ) : null}
        <PreviewPlaceholder shown={!revealed} bgColor={bgColor} />
      </Body>
    </Overlay>,
    document.body,
  );
};

export default MobileSitePreview;
